import React, { useReducer } from "react";
import PropTypes from "prop-types";

import CommentFilterContext from "./Context";

const initialFilterState = {
  commentRequested: false,
  newStandaloneDraft: undefined,
  taskUuid: undefined,
  clientWorkflowUuid: undefined,
  relatedEntityTitle: "",
  relatedEntityType: "",
};

const reducer = (state, action) => {
  switch (action.type) {
    case "CLEAR_FILTERS":
      return {
        ...state,
        taskUuid: undefined,
        clientWorkflowUuid: undefined,
        relatedEntityTitle: "",
        relatedEntityType: "",
      };
    case "FILTER_BY":
      return {
        ...state,
        taskUuid: action.taskUuid,
        clientWorkflowUuid: action.clientWorkflowUuid,
        relatedEntityTitle: action.relatedEntityTitle,
        relatedEntityType: action.entityType,
      };
    case "REQUEST_COMMENT":
      return {
        ...state,
        commentRequested: true,
      };
    case "FULFILL_COMMENT":
      return {
        ...state,
        commentRequested: false,
      };
    case "SET_NEW_STANDALONE_DRAFT":
      return {
        ...state,
        newStandaloneDraft: action.draftGid,
      };
    case "WORKFLOW_RENAMED":
      return state.clientWorkflowUuid === action.workflowUuid
        ? {
            ...state,
            relatedEntityTitle: action.newTitle,
          }
        : state;
    case "TASK_RENAMED":
      return state.taskUuid === action.taskUuid
        ? {
            ...state,
            relatedEntityTitle: action.newTitle,
          }
        : state;
    case "TASK_REMOVED":
      return state.taskUuid === action.taskUuid
        ? {
            ...state,
            taskUuid: undefined,
            clientWorkflowUuid: undefined,
            relatedEntityTitle: "",
          }
        : state;
    default:
      return state;
  }
};

const CommentFilterContextDataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialFilterState);

  const filterBy = (entity) => {
    const isTask = entity.type === "task";
    dispatch({
      type: "FILTER_BY",
      entityType: entity.type,
      taskUuid: isTask ? entity.id : undefined,
      clientWorkflowUuid: isTask ? entity.parentId : entity.id,
      relatedEntityTitle: entity.name,
    });
  };

  const clearAllFilters = () => {
    dispatch({ type: "CLEAR_FILTERS" });
  };

  return (
    <CommentFilterContext.Provider
      value={{
        commentRequested: state.commentRequested,
        newStandaloneDraft: state.newStandaloneDraft,
        taskUuid: state.taskUuid,
        clientWorkflowUuid: state.clientWorkflowUuid,
        relatedEntityTitle: state.relatedEntityTitle,
        relatedEntityType: state.relatedEntityType,
        clearAllFilters: clearAllFilters,
        applyFilter: filterBy,
        openCommentDialog: () => {
          dispatch({ type: "REQUEST_COMMENT" });
        },
        fulfillComment: () => {
          dispatch({ type: "FULFILL_COMMENT" });
        },
        setActiveStandaloneDraft: (draftGid) => {
          dispatch({
            type: "SET_NEW_STANDALONE_DRAFT",
            draftGid: draftGid,
          });
        },
        taskRemoved: (taskUuid) => {
          dispatch({ type: "TASK_REMOVED", taskUuid: taskUuid });
        },
        taskRenamed: (taskUuid, newTitle) => {
          dispatch({
            type: "TASK_RENAMED",
            taskUuid: taskUuid,
            newTitle: newTitle,
          });
        },
        workflowRenamed: (workflowUuid, newTitle) => {
          dispatch({
            type: "WORKFLOW_RENAMED",
            workflowUuid: workflowUuid,
            newTitle: newTitle,
          });
        },
      }}
    >
      {children}
    </CommentFilterContext.Provider>
  );
};

CommentFilterContextDataProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default CommentFilterContextDataProvider;
